import type { ContentPage } from "@/lib/content/types";

// PROOF PAGE for the Phase 3 engine. This is a brand-new feature page that did
// not exist before, so it touches nothing. It renders at
// /hrms-software/expense-management/ via the catch-all route — built entirely
// from this data file (no hand-coded JSX).

export const expenseManagement: ContentPage = {
  slug: "expense-management",
  template: "feature",
  // Opt into auto-nav (footer "Features") + provides sitemap lastmod. Demonstrates
  // that adding one content file auto-wires the page into nav and the sitemap.
  nav: { label: "Expense Management", group: "features", order: 50 },
  updatedAt: "2026-08-03",
  seo: {
    title: "Expense Management Software | GetBiz HRMS",
    description:
      "Automate employee expense claims, approvals, and reimbursements with GetBiz HRMS. Faster approvals, full visibility, and zero spreadsheets.",
    keywords: [
      "expense management",
      "employee expense software",
      "reimbursement automation",
      "HRMS expense claims",
    ],
    ogImage: "/assets/data/seoimages/Home.webp",
  },
  relatedBlogsKeyword: "HRMS, expense, payroll",
  sections: [
    {
      type: "hero",
      title: "Expense Management, fully automated",
      subtitle:
        "Let employees submit claims in seconds, give managers one-click approvals, and reimburse on time — all inside GetBiz HRMS.",
      image: "/assets/images/img/it_solutions_itsm.png",
      cta: { label: "Book a demo", href: "/book-demo/" },
      secondaryCta: { label: "View pricing", href: "/pricing/" },
    },
    { type: "clientLogos" },
    { type: "facts" },
    {
      type: "featureGrid",
      title: "Everything you need to control spend",
      subtitle:
        "From claim submission to reimbursement, every step is tracked and automated.",
      items: [
        {
          title: "One-tap claim submission",
          description:
            "Employees snap a receipt and submit — categories, tax, and policy checks happen automatically.",
        },
        {
          title: "Multi-level approvals",
          description:
            "Route claims through the right managers with configurable approval chains and reminders.",
        },
        {
          title: "Policy enforcement",
          description:
            "Flag out-of-policy spend before it's approved, with per-category limits and rules.",
        },
        {
          title: "Faster reimbursements",
          description:
            "Approved claims flow straight into payroll for on-time, accurate reimbursement.",
        },
        {
          title: "Full audit trail",
          description:
            "Every claim, approval, and payment is logged for compliance and reporting.",
        },
        {
          title: "Real-time dashboards",
          description:
            "See spend by team, category, and period — no spreadsheets required.",
        },
      ],
    },
    {
      type: "imageText",
      title: "Approvals that don't slow anyone down",
      body:
        "Managers approve or reject from web or mobile in a single tap. Automatic reminders keep claims moving, and finance gets full visibility into what's pending, approved, and paid.",
      image: "/assets/images/img/it_solutions_itsm.webp",
      imageSide: "right",
      cta: { label: "See how it works", href: "/book-demo/" },
    },
    { type: "steps" },
    { type: "pricing" },
    { type: "testimonials" },
    {
      type: "faq",
      title: "Expense Management FAQs",
      items: [
        {
          question: "Can employees submit expenses from their phone?",
          answer:
            "Yes. Employees can capture receipts and submit claims from any device, and managers can approve on the go.",
        },
        {
          question: "Does it integrate with payroll?",
          answer:
            "Approved reimbursements flow directly into GetBiz payroll, so employees are paid accurately and on time.",
        },
        {
          question: "Can we enforce spending policies?",
          answer:
            "Yes — set per-category limits and rules, and out-of-policy claims are flagged automatically before approval.",
        },
      ],
    },
    {
      type: "cta",
      title: "Ready to ditch expense spreadsheets?",
      subtitle:
        "See GetBiz Expense Management in action with a free, no-obligation demo.",
      button: { label: "Book your demo", href: "/book-demo/" },
    },
  ],
};
